import axiosService from "./axiosService";
import { Response } from "../interfaces/Response";
import { DpeComplet } from "../interfaces/DpeComplet";
import { DpeStats } from "../interfaces/DpeStats";

const API_DPE_URL = process.env.REACT_APP_API_DPE_URL;

interface PaginationParams {
    page: number;
    size: number;
}

interface PaginatedResponse<T> {
    content: T[];
    totalElements: number;
    totalPages: number;
    size: number;
    number: number;
}

export const getDpes = async ({ page = 0, size = 20 }: PaginationParams): Promise<PaginatedResponse<DpeComplet>> => {
    try {
        const response = await axiosService.get(
            `${API_DPE_URL}/api/dpe/residentiel/stored_dpe/list`,
            {
                params: {
                    page: page + 1, // +1 car l'API commence à 1 mais MUI à 0
                    size
                }
            }
        );

        if (response.data && Array.isArray(response.data.content)) {
            return {
                content: response.data.content,
                totalElements: response.data.totalElements || 0,
                totalPages: response.data.totalPages || 1,
                size: response.data.size || size,
                number: response.data.number || page
            };
        }

        throw new Error('Format de réponse invalide');

    } catch (error) {
        console.error('Erreur lors de la récupération des DPEs', error);
        throw error;
    }
};

export const putDpeFile = async (numeroDPE : string, file : File): Promise<Response> => {
    try {
        const token = sessionStorage.getItem('tokenAdminUI');
        
        // Création du FormData
        const formData = new FormData();
        formData.append('numeroDPE', numeroDPE);
        formData.append('file', file);

        const response = await axiosService.post(
            `${API_DPE_URL}/api/dpe/residentiel/file/import`, 
            formData,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data', // Important pour l'upload de fichier
                }
            }
        );
        return response.data;
    } catch (error) {
        console.error('Erreur lors de l\'import du fichier DPE', error);
        throw error;
    }
};

export const deleteDpeFile = async ( numeroDPEs: string[]): Promise<Response> => {
    try {
        const token = sessionStorage.getItem('tokenAdminUI');
        const response = await axiosService.delete<Response>(`${API_DPE_URL}/api/dpe/residentiel`, {
            headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            },
            data: numeroDPEs, // Données envoyées dans le corps de la requête
        });
        return response.data;
    } catch (error: any) {
      console.error('Error while deleting DPEs:', error);
      throw new Error(error.response?.data?.message || 'An unexpected error occurred');
    }
};

export const exportTextVerificationAPE = async (numerodpe: string): Promise<void> => {
    try {
        const token = sessionStorage.getItem('tokenAdminUI');

        // Formater le contenu avec des sauts de ligne et indentation
        const formatMapToText = (data: Record<string, string>): string => {
            let textContent = '';
            // Convertir l'objet en entrées clé-valeur et les formater
            Object.entries(data).forEach(([key, value]) => {
                textContent += `${key}: ${value}\n`;
            });
            return textContent;
        };
        

        // Requête pour télécharger le fichier texte
        const response = await axiosService.get(`${API_DPE_URL}/api/dpe/residentiel/verificationape/${numerodpe}/download/texte`, {
            headers: {
                'Authorization': `Bearer ${token}`,
            },
            //responseType: 'blob',
        });

        if (response.data) {
            
            // Convertir et formater les données
            const textContent = formatMapToText(response.data);
            
            // Créer le Blob avec le contenu formaté
            const fileBlob = new Blob([textContent], { type: 'text/plain;charset=utf-8' });
            
            // Créer et déclencher le téléchargement
            const url = window.URL.createObjectURL(fileBlob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `verification_APE_${numerodpe}.txt`;
            
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            
            // Nettoyer l'URL
            window.URL.revokeObjectURL(url);
        } else {
            alert('Erreur lors du téléchargement du fichier');
        }

    } catch (error) {
        console.error(`Erreur lors de l'export du fichier de vérification pour le DPE n° ${numerodpe}`, error);
        throw error;
    }
};

export const searchDpesByNumeroDPE = async (numeroDPE: string, logiciel: string, version: string, typeBatiment: string, zoneClimatique: string, classeAltitude: string, page: number, size: number): Promise<PaginatedResponse<DpeComplet>> => {
    try {
        const token = sessionStorage.getItem('tokenAdminUI');
        const response = await axiosService.get(
            `${API_DPE_URL}/api/dpe/residentiel/stored_dpe/search`,
            {
                params: {
                    numeroDPE,
                    logiciel,
                    version,
                    typeBatiment,
                    zoneClimatique,
                    classeAltitude,
                    page: page + 1,
                    size
                },
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            }
        );

        if (response.data) {
            return {
                content: response.data.content,
                totalElements: response.data.totalElements,
                totalPages: response.data.totalPages,
                size: response.data.size,
                number: response.data.number
            };
        }

        throw new Error('Format de réponse invalide');
    } catch (error: any) {
        console.error('Erreur lors de la recherche des DPEs:', error);
        throw new Error(error.response?.data?.message || 'Erreur lors de la recherche des DPEs');
    }
};

export const downloadDpeExcel = async (dpeId: string): Promise<void> => {
    try {
        const token = sessionStorage.getItem('tokenAdminUI');
        const response = await axiosService.get(
            `${API_DPE_URL}/api/dpe/${dpeId}/export/excel`,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                responseType: 'blob',
            }
        );

        // Créer un blob à partir de la réponse
        const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });

        // Créer une URL pour le blob
        const url = window.URL.createObjectURL(blob);

        // Créer un élément <a> temporaire pour le téléchargement
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `DPE_${dpeId}.xlsx`); // Nom du fichier
        document.body.appendChild(link);
        link.click();

        // Nettoyer
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

    } catch (error) {
        console.error('Erreur lors du téléchargement du fichier Excel:', error);
        throw new Error('Erreur lors du téléchargement du fichier Excel');
    }
};

export const downloadFilteredDpes = async (
    numeroDPE?: string,
    logiciel?: string,
    version?: string,
    typeBatiment?: string,
    zoneClimatique?: string,
    altitude?: string
): Promise<void> => {
    try {
        const token = sessionStorage.getItem('tokenAdminUI');
        const response = await axiosService.get(
            `${API_DPE_URL}/api/dpe/calcul/export/excel`,
            {
                params: {
                    numeroDPE,
                    logiciel,
                    version,
                    typeBatiment,
                    zoneClimatique,
                    altitude
                },
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
                responseType: 'blob'
            }
        );

        const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `dpes_filtres_${new Date().toISOString().split('T')[0]}.xlsx`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

    } catch (error) {
        console.error('Erreur lors du téléchargement:', error);
        throw error;
    }
};

export const getDpeStats = async (): Promise<DpeStats> => {
    try {
        const token = sessionStorage.getItem('tokenAdminUI');
        
        if (!token) {
            throw new Error('Token non trouvé');
        }

        const response = await axiosService.get<DpeStats>(
            `${API_DPE_URL}/api/dpe/statistiques`,
            {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            }
        );

        // Log pour debug
        console.debug('Statistiques DPE reçues:', response.data);

        // Vérification et transformation des données si nécessaire
        return response.data;

    } catch (error: any) {
        console.error('Erreur lors de la récupération des statistiques DPE:', error);
        
        // Gestion spécifique des erreurs
        if (error.response?.status === 401) {
            throw new Error('Session expirée, veuillez vous reconnecter');
        }
        
        if (error.response?.status === 403) {
            throw new Error('Accès non autorisé aux statistiques');
        }

        throw new Error('Erreur lors de la récupération des statistiques DPE');
    }
};

/*
export const createDpe = async (dpe: Partial<Dpe>) => {
    const response = await axios.post('/dpes', dpe);
    return response.data;
};

export const updateDpe = async (id: string, dpe: Partial<Dpe>) => {
    const response = await axios.put(`/dpes/${id}`, dpe);
    return response.data;
};

export const deleteDpe = async (id: string) => {
    const response = await axios.delete(`/dpes/${id}`);
    return response.data;
};
*/
